import React, { useState, useEffect } from 'react';
import api from '../services/api';

const BoxEntry = () => {
  const [boxEntries, setBoxEntries] = useState([]);
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [cust_name, setCust_name] = useState('');
  const [vehicle_no, setVehicle_no] = useState('');
  const [sending_box, setSending_box] = useState('');
  const [receive_box, setReceive_box] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchBoxEntries();
  }, []);

  const fetchBoxEntries = async () => {
    setLoading(true);
    try {
      const res = await api.get('/boxes');
      setBoxEntries(res.data);
    } catch (err) {
      setError('Failed to fetch box entries');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await api.post('/boxes', {
        date,
        cust_name,
        vehicle_no,
        sending_box: parseInt(sending_box) || 0,
        receive_box: parseInt(receive_box) || 0
      });
      setSuccess('Box tracking record saved successfully');
      setCust_name('');
      setVehicle_no('');
      setSending_box('');
      setReceive_box('');
      fetchBoxEntries();
    } catch (err) {
      setError('Failed to save box entry');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete box tracking entry?')) return;
    try {
      await api.delete(`/boxes/${id}`);
      setSuccess('Box entry deleted');
      fetchBoxEntries();
    } catch (err) {
      setError('Failed to delete box entry');
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
        <div>
          <h3 className="fw-bold mb-1 text-primary">
            <i className="bi bi-box-seam me-2"></i>Fish Box Entry Tracker (बॉक्स जमा-खर्च)
          </h3>
          <p className="text-muted mb-0">Track sending & receiving fish boxes with customer & transport vehicle details</p>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {success && <div className="alert alert-success py-2">{success}</div>}

      <div className="row g-4">
        {/* FORM */}
        <div className="col-12 col-md-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-primary text-white fw-bold">
              <i className="bi bi-plus-circle me-2"></i>New Box Entry
            </div>
            <div className="card-body">
              <form onSubmit={handleSave}>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Customer / Party Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. BABLU / GORAKH LANKE"
                    value={cust_name}
                    onChange={(e) => setCust_name(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Vehicle Registration No</label>
                  <input
                    type="text"
                    className="form-control text-uppercase"
                    placeholder="e.g. MH-06-AC-1234"
                    value={vehicle_no}
                    onChange={(e) => setVehicle_no(e.target.value)}
                  />
                </div>

                <div className="row g-2 mb-4">
                  <div className="col-6">
                    <label className="form-label fw-semibold text-danger">Sending Boxes</label>
                    <input
                      type="number"
                      className="form-control fw-bold text-danger"
                      placeholder="0"
                      value={sending_box}
                      onChange={(e) => setSending_box(e.target.value)}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-semibold text-success">Received Boxes</label>
                    <input
                      type="number"
                      className="form-control fw-bold text-success"
                      placeholder="0"
                      value={receive_box}
                      onChange={(e) => setReceive_box(e.target.value)}
                    />
                  </div>
                </div>

                <button type="submit" className="btn btn-primary w-100 fw-bold shadow-sm">
                  <i className="bi bi-check-circle me-1"></i> Save Box Record
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* LOG TABLE */}
        <div className="col-12 col-md-8">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white py-3">
              <h5 className="fw-bold mb-0 text-dark">
                Box Tracker Log ({boxEntries.length})
              </h5>
            </div>
            <div className="table-responsive" style={{ maxHeight: '600px' }}>
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>Date</th>
                    <th>Customer Name</th>
                    <th>Vehicle No</th>
                    <th>Sending Boxes</th>
                    <th>Received Boxes</th>
                    <th>Net Balance</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4 text-muted">
                        <span className="spinner-border spinner-border-sm me-2"></span> Loading box records...
                      </td>
                    </tr>
                  ) : boxEntries.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4 text-muted">
                        No box records found.
                      </td>
                    </tr>
                  ) : (
                    boxEntries.map((b) => {
                      const net = (b.sending_box || 0) - (b.receive_box || 0);
                      return (
                        <tr key={b.id}>
                          <td>{b.date}</td>
                          <td className="fw-semibold text-primary">{b.cust_name}</td>
                          <td>{b.vehicle_no || '-'}</td>
                          <td className="text-danger fw-bold">{b.sending_box || 0}</td>
                          <td className="text-success fw-bold">{b.receive_box || 0}</td>
                          <td>
                            <span className={`badge ${net >= 0 ? 'bg-warning text-dark' : 'bg-success'}`}>
                              {net >= 0 ? `+${net} Pending` : `${net} Cleared`}
                            </span>
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleDelete(b.id)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoxEntry;
